import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@mui/material";
import { PrintLocationLabelsAction, PrintLocationLabelsDialog } from "./PrintLocationLabelsDialog";
import { UploadLocationsDialog } from "./UploadLocationsDialog";
import { FullPagePaperLayout } from "../Layouts/FullPagePaperLayout";
import { PageBreadcrumbs, PageTitle, PageActions, PageSubtitle } from "../Layouts/components/PageTitleContainer";
import {
  getGridStringOperators,
  GridFilterOperator,
  GridRenderCellParams,
  GridValueGetterParams,
} from "@mui/x-data-grid-pro";
import { LocationManagementLocation } from "types/locationManagement/location";
import { RevivnDatabaseDataGrid } from "components/DataGrid/RevivnDatabaseDataGrid";
import { dateColDef } from "components/DataGrid/columnDefs/dateColDef";

// The path column shows "Fresno / FFLAA03C00". These are the text operators the list accepts for that string.
const pathOperators = getGridStringOperators().filter((operator) =>
  ["contains", "equals", "startsWith", "endsWith"].includes(operator.value),
);

// Matches the column: direct child locations, or assets on this location and every location inside it.
const countPresenceOperators: GridFilterOperator[] = [
  {
    label: "greater than 0",
    value: "greaterThanZero",
    getApplyFilterFn: () => null,
    requiresFilterValue: false,
  },
  {
    label: "not greater than 0",
    value: "notGreaterThanZero",
    getApplyFilterFn: () => null,
    requiresFilterValue: false,
  },
];

const LocationList: React.FC = () => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [printCodes, setPrintCodes] = useState<string[] | null>(null);
  const getBulkActions = useCallback(() => [<PrintLocationLabelsAction onSelect={setPrintCodes} />], []);

  return (
    <FullPagePaperLayout>
      <PageBreadcrumbs links={[{ label: "Warehouse" }, { label: "Locations", path: "/locations" }]} />
      <PageTitle>Locations</PageTitle>
      <PageSubtitle>
        Locations represent specific, real-world facility sites like shelves, desks, and floor spots. Every location
        must be unique. Each warehouse has a fixed location with its name. That location is the parent other locations
        are filed into, and it can&apos;t be moved. A location without a type can be placed inside another one.
      </PageSubtitle>
      <PageActions>
        <Button variant="contained" color="primary" component={Link} to="/locations/new">
          Create Location
        </Button>
        <Button variant="outlined" onClick={() => setUploadOpen(true)}>
          Upload locations
        </Button>
      </PageActions>
      {uploadOpen && <UploadLocationsDialog open onClose={() => setUploadOpen(false)} />}
      <PrintLocationLabelsDialog codes={printCodes} onClose={() => setPrintCodes(null)} />
      <RevivnDatabaseDataGrid
        checkboxSelection
        getBulkActions={getBulkActions}
        url="/location_management/locations"
        syncStateWithSearchParams
        defaultSort="-created_at"
        columns={[
          { field: "id", headerName: "ID", width: 90, filterable: false },
          {
            field: "created_at",
            headerName: "Created",
            width: 140,
            filterable: false,
            valueGetter: (params: GridValueGetterParams<LocationManagementLocation>) => params.row.createdAt,
            ...dateColDef,
          },
          {
            field: "code",
            headerName: "Location Name",
            width: 200,
            filterable: true,
            renderCell: (params: GridRenderCellParams<LocationManagementLocation>) => (
              <Link to={`/locations/${params.row.id}/edit`}>{params.row.code}</Link>
            ),
          },
          {
            field: "locationType",
            headerName: "Location Type",
            width: 150,
            valueGetter: (params: GridValueGetterParams<LocationManagementLocation>) =>
              params.row.locationType?.name || "",
            filterable: true,
            sortable: false,
            renderCell: (params: GridRenderCellParams<LocationManagementLocation>) =>
              params.row.locationType ? (
                <Link to={`/location-types/${params.row.locationType.id}/edit`}>{params.row.locationType.name}</Link>
              ) : params.row.warehouseId ? (
                "No type (warehouse)"
              ) : (
                "No type (fixed)"
              ),
          },
          {
            field: "organizationName",
            headerName: "Client",
            width: 200,
            sortable: false,
            filterable: true,
            valueGetter: (params: GridValueGetterParams<LocationManagementLocation>) =>
              params.row.organizationName || "Shared",
          },
          {
            field: "locationKind",
            headerName: "Location Kind",
            width: 160,
            valueGetter: (params: GridValueGetterParams<LocationManagementLocation>) =>
              params.row.locationKind?.name || "",
            filterable: true,
            sortable: false,
            renderCell: (params: GridRenderCellParams<LocationManagementLocation>) =>
              params.row.locationKind ? (
                <Link to={`/location-kinds/${params.row.locationKind.id}/edit`}>{params.row.locationKind.name}</Link>
              ) : (
                "No kind"
              ),
          },
          {
            field: "parent",
            headerName: "Inside",
            width: 150,
            filterable: true,
            sortable: false,
            valueGetter: (params: GridValueGetterParams<LocationManagementLocation>) => params.row.parent?.code || "",
            renderCell: (params: GridRenderCellParams<LocationManagementLocation>) =>
              params.row.parent ? (
                <Link to={`/locations/${params.row.parent.id}/edit`}>{params.row.parent.code}</Link>
              ) : (
                ""
              ),
          },
          {
            // "View locations inside" filters on this field. It stays filterable: resizing a column
            // updates the column list, and the grid then deletes filters on columns that are not filterable.
            // The Columns panel drops hideable: false columns.
            field: "inside",
            headerName: "Inside location",
            filterOperators: getGridStringOperators().filter((operator) => operator.value === "equals"),
            sortable: false,
            hide: true,
            hideable: false,
          },
          {
            field: "path",
            headerName: "Path",
            width: 260,
            filterOperators: pathOperators,
            sortable: false,
          },
          {
            field: "childrenCount",
            headerName: "Locations inside",
            type: "number",
            width: 140,
            filterable: true,
            filterOperators: countPresenceOperators,
            sortable: false,
          },
          {
            field: "inventoriesCount",
            headerName: "Assets inside",
            type: "number",
            width: 120,
            filterable: true,
            filterOperators: countPresenceOperators,
            sortable: false,
          },
          {
            field: "description",
            headerName: "Description",
            width: 150,
            filterable: false,
          },
        ]}
        pageSize={25}
      />
    </FullPagePaperLayout>
  );
};

export default LocationList;
